import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

const PHONE_NUMBER = "+971541873527";
const WHATSAPP_LINK = `https://wa.me/971541873527?text=Hello%20Guru%20Maa,%20I%20would%20like%20to%20consult%20about%20my%20horoscope.%20Please%20guide%20me.`;

const ContactForm = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error("Please complete the required fields");
      return;
    }

    // Create WhatsApp message with form data
    const message = `Hello Guru Maa,%0A%0AName: ${formData.name}%0APhone: ${formData.phone}${formData.email ? `%0AEmail: ${formData.email}` : ""}${formData.message ? `%0AMessage: ${formData.message}` : ""}%0A%0AI would like to schedule a consultation.`;

    window.open(`https://wa.me/971541873527?text=${message}`, "_blank");

    setIsSubmitted(true);
    toast.success("Redirecting to WhatsApp...");

    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", phone: "", email: "", message: "" });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Call Us",
      value: "+971541873527",
      href: `tel:${PHONE_NUMBER}`,
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: "Chat Now",
      href: WHATSAPP_LINK,
    },
    {
      icon: Clock,
      label: "Availability",
      value: "9 AM - 9 PM IST",
      href: null,
    },
    {
      icon: MapPin,
      label: "Consultation",
      value: "Online & In-Person",
      href: null,
    },
  ];

  return (
    <section
      id="contact"
      className="relative py-20 md:py-32"
      data-testid="contact-section"
      ref={ref}
    >
      {/* Section Divider */}
      <div className="section-divider mb-20" />

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#8B5CF6]/5 to-transparent" />
      <div className="cosmic-glow absolute top-1/4 right-0 opacity-15" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
            Get in Touch
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
            Begin Your{" "}
            <span className="text-gold-gradient">Spiritual Journey</span>
          </h2>
          <p className="mt-4">
            Take the first step towards transforming your destiny
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-2xl font-bold mb-6">Connect with Guru Maa</h3>
            <p className="text-[#94A3B8] mb-8">
              Whether you seek guidance for love, career, health, or spiritual
              growth, Guru Maa is here to illuminate your path. Reach out today
              for a transformative consultation.
            </p>

            {/* Contact Cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                const content = (
                  <div
                    className={`p-5 rounded-xl border border-white/10 bg-white/5 ${
                      item.href
                        ? "hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all cursor-pointer"
                        : ""
                    }`}
                    data-testid={`contact-info-${index}`}
                  >
                    <Icon className="text-[#D4AF37] mb-3" size={24} />
                    <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1">
                      {item.label}
                    </p>
                    <p className="text-[#F8FAFC] font-medium">{item.value}</p>
                  </div>
                );

                return item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={item.label}>{content}</div>
                );
              })}
            </div>

            {/* Quick CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-3 whatsapp-btn py-4 rounded-full font-bold text-lg"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={22} />
                <span>WhatsApp Now</span>
              </a>
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="flex-1 flex items-center justify-center gap-3 cta-gold"
                data-testid="contact-call-btn"
              >
                <Phone size={22} />
                <span>Call Now</span>
              </a>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="contact-form">
              <h3 className="text-xl font-bold mb-2">Quick Enquiry Form</h3>
              <p className="text-[#94A3B8] text-sm mb-6">
                Submit your details and we will connect with you promptly
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <CheckCircle className="text-green-500 mb-4" size={64} />
                  <h4 className="text-xl font-bold text-[#F8FAFC] mb-2">
                    Thank You!
                  </h4>
                  <p className="text-[#94A3B8] text-center">
                    Redirecting you to WhatsApp...
                  </p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  <div>
                    <label className="block text-[#F8FAFC] text-sm font-medium mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="cosmic-input"
                      required
                      data-testid="form-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#F8FAFC] text-sm font-medium mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className="cosmic-input"
                      required
                      data-testid="form-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#F8FAFC] text-sm font-medium mb-2">
                      Email Address{" "}
                      <span className="text-[#94A3B8] text-xs">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className="cosmic-input"
                      data-testid="form-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#F8FAFC] text-sm font-medium mb-2">
                      Message{" "}
                      <span className="text-[#94A3B8] text-xs">(Optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Briefly describe your inquiry..."
                      rows={4}
                      className="cosmic-input resize-none"
                      data-testid="form-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-3 cta-gold py-4"
                    data-testid="form-submit-btn"
                  >
                    <Send size={20} />
                    <span>Submit & Connect via WhatsApp</span>
                  </button>

                  <p className="text-[#94A3B8] text-xs text-center mt-4">
                    By submitting, you consent to being contacted via WhatsApp
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
